import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Coins, Loader2 } from "lucide-react";
import { api, errMsg, money, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { t } from "@/lib/i18n";
import { PayPalCheckout } from "@/components/PayPalCheckout";

export default function Wallet() {
  const { user, meta, refresh } = useAuth();
  const [ledger, setLedger] = useState(null);
  const [selected, setSelected] = useState("");
  const [clientId, setClientId] = useState(null);
  const [fx, setFx] = useState(null);

  const loadLedger = () => api.get("/credits/ledger").then((r) => setLedger(r.data)).catch((e) => { setLedger([]); toast.error(errMsg(e)); });
  useEffect(() => { loadLedger(); api.get("/payments/paypal/config").then((r) => setClientId(r.data.client_id)).catch(() => setClientId("")); api.get("/fx/rate").then((r) => setFx(r.data)).catch(() => {}); }, []);
  const local = (usd) => (fx?.rate && fx.currency !== "USD" ? `≈ ${money(usd * fx.rate, fx.currency)}` : null);

  const paid = () => { setSelected(""); refresh(); loadLedger(); };

  const action = user.role === "shipper" ? "load post" : "bid";

  return (
    <div>
      <PageHeader kicker={t("Credits · $10 each")} title={t("Wallet")} />
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4">
          <div className="rounded-lg bg-slate-900 text-white p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t("Available balance")}</p>
            <p className="font-display font-black text-6xl mt-3 flex items-center gap-3" data-testid="wallet-balance"><Coins className="w-10 h-10 text-amber-400" />{user.credits}</p>
            <p className="text-sm text-slate-400 mt-3">{t("1 credit = $10 = 1 {action}", { action: t(action) })}</p>
            {local(10) && <p className="text-xs text-slate-400 mt-2" data-testid="local-rate-note">{t("Prices shown in {currency} are estimates. You are charged in USD; your bank sets the final amount.", { currency: fx.currency })}</p>}
          </div>
        </div>
        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
          {meta.packages.map((p) => (
            <div key={p.id} className="hf-card p-5 flex flex-col justify-between gap-4 hover:border-amber-500 transition-colors" data-testid={`package-${p.id}`}>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{t(p.label)}</p>
                <p className="font-display font-black text-3xl mt-1">{p.credits} {t("credit")}{p.credits > 1 ? "s" : ""}</p>
                <p className="text-sm text-slate-600">{p.credits} × {t(action)}</p>
                {local(p.amount) && <p className="font-mono text-xs text-amber-700 mt-1" data-testid={`local-price-${p.id}`}>{local(p.amount)}</p>}
              </div>
              {selected === p.id && clientId ? (
                <div>
                  <p className="text-xs text-slate-500 mb-2">{t("Pay {amount} with PayPal or a debit/credit card", { amount: money(p.amount) })}</p>
                  <PayPalCheckout clientId={clientId} pkg={p.id} onPaid={paid} />
                  <button onClick={() => setSelected("")} className="text-xs text-slate-500 underline mt-1" data-testid={`btn-cancel-buy-${p.id}`}>{t("Cancel")}</button>
                </div>
              ) : (
                <button disabled={clientId === null} onClick={() => clientId ? setSelected(p.id) : toast.error(t("Payments are not configured yet"))} className="hf-btn-primary w-full" data-testid={`btn-buy-credits-${p.id}`}>
                  {clientId === null ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {t("Buy")} · {money(p.amount)}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="hf-card mt-8">
        <p className="px-5 py-4 border-b font-display font-bold">{t("Credit history")}</p>
        {ledger === null && <p className="p-5 font-mono text-xs text-slate-500">{t("LOADING…")}</p>}
        {ledger?.length === 0 && <p className="p-5 text-sm text-slate-500">{t("No transactions yet.")}</p>}
        <ul className="divide-y" data-testid="credit-ledger">
          {ledger?.map((l) => (
            <li key={l.id} className="px-5 py-3 flex justify-between gap-4 text-sm">
              <span className="min-w-0"><span className="block break-words">{l.reason}</span><span className="font-mono text-[10px] text-slate-400">{fmtTime(l.created_at)}</span></span>
              <span className={`font-mono font-bold shrink-0 ${l.delta > 0 ? "text-emerald-600" : "text-slate-900"}`}>{l.delta > 0 ? "+" : ""}{l.delta}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
